import { useEffect, useRef, useState } from "react";
import { Tag, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { api, formatUSD } from "@/lib/api";

/**
 * Discreet promo code input with live discount preview.
 *
 * Renders only a small chip until clicked. When `previewContext` is provided
 * (`{type, reference_id}`), each keystroke (debounced 350ms) hits
 * `/api/promo/preview` and shows inline feedback so the user sees the savings
 * BEFORE redirecting to Stripe. If `previewContext` is omitted, the field stays
 * silent and behaves like a plain input (back-compat).
 */
export function PromoCodeField({ value, onChange, disabled, previewContext }) {
  const [open, setOpen] = useState(false);
  // preview: null | {state: "checking"} | {state: "valid", ...} | {state: "error", message}
  const [preview, setPreview] = useState(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const code = (value || "").trim();
    if (!previewContext || !code) {
      setPreview(null);
      return;
    }
    setPreview({ state: "checking" });
    const myReq = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/promo/preview", {
          code,
          type: previewContext.type,
          reference_id: previewContext.reference_id,
        });
        if (myReq !== reqIdRef.current) return; // a newer keystroke superseded us
        if (data.valid) {
          setPreview({ state: "valid", ...data });
        } else {
          setPreview({ state: "error", message: data.error || "Invalid code" });
        }
      } catch (e) {
        if (myReq !== reqIdRef.current) return;
        setPreview({ state: "error", message: e.response?.data?.detail || "Couldn't check code" });
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [value, previewContext]);

  const reset = () => {
    onChange("");
    setOpen(false);
    setPreview(null);
  };

  return (
    <div className="mt-3" data-testid="promo-code-field">
      {!open && !value ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="promo-open"
          className="inline-flex items-center gap-1.5 border border-[#FF2D2D]/40 hover:border-[#FF2D2D] bg-[#FF2D2D]/5 hover:bg-[#FF2D2D]/10 text-white text-[11px] uppercase tracking-wider px-3 py-1.5 transition-colors"
        >
          <Tag size={12} weight="fill" className="text-[#FF2D2D]" /> Have a promo code?
        </button>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-[#FF2D2D]" weight="fill" />
            <input
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value.toUpperCase())}
              disabled={disabled}
              data-testid="promo-input"
              placeholder="Enter code"
              autoComplete="off"
              spellCheck="false"
              className={`flex-1 bg-[#050505] border px-3 py-1.5 text-sm uppercase tracking-wider focus:border-[#FF2D2D] outline-none transition-colors ${
                preview?.state === "valid" ? "border-[#28C878]" :
                preview?.state === "error" ? "border-[#FF2D2D]" :
                "border-[#262626]"
              }`}
            />
            <button
              type="button"
              onClick={reset}
              data-testid="promo-close"
              className="text-xs text-[#a3a3a3] hover:text-white"
            >
              Clear
            </button>
          </div>
          {previewContext && preview && (
            <div className="mt-2 text-xs" data-testid="promo-preview">
              {preview.state === "checking" && (
                <div className="flex items-center gap-1.5 text-[#a3a3a3]" data-testid="promo-preview-checking">
                  <CircleNotch size={12} className="animate-spin" /> Checking code…
                </div>
              )}
              {preview.state === "valid" && (
                <div
                  className="flex items-baseline gap-1.5 text-[#28C878]"
                  data-testid="promo-preview-valid"
                  title={`Original ${formatUSD(preview.original_amount)} → ${formatUSD(preview.final_amount)}`}
                >
                  <CheckCircle size={12} weight="fill" />
                  <span className="font-semibold">
                    {preview.discount_type === "flat"
                      ? `${formatUSD(preview.savings)} off`
                      : `${Math.round(preview.discount * 100)}% off`}
                  </span>
                  <span className="text-[#a3a3a3]">
                    — you save <span className="text-white tabular-nums">{formatUSD(preview.savings)}</span>
                    {", pay "}
                    <span className="text-white tabular-nums">{formatUSD(preview.final_amount)}</span>
                  </span>
                </div>
              )}
              {preview.state === "error" && (
                <div className="flex items-center gap-1.5 text-[#FF2D2D]" data-testid="promo-preview-error">
                  <XCircle size={12} weight="fill" /> {preview.message}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
